import { ReactNode } from 'react'
import { Box, Collapse, IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material'
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material'

/**
 * The row expander a table uses at tablet widths for the columns it drops.
 * Pair it with getCompactTableSx (utils/compactTableStyles), which styles the
 * expander row through the `data-expander` attribute set here.
 *
 * A table using it renders ExpanderHeaderCell last in its head row,
 * ExpanderToggleCell last in each data row, and an ExpanderRow straight after
 * each data row.
 */

// Wide enough for the small IconButton plus the tablet cell padding.
const EXPANDER_COLUMN_WIDTH = 46

export function ExpanderHeaderCell() {
  return (
    <TableCell sx={{ width: EXPANDER_COLUMN_WIDTH }}>
      {/* Sizes are strings on purpose: in sx a bare 1 means 100%. */}
      <Box
        component="span"
        sx={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
      >
        Details
      </Box>
    </TableCell>
  )
}

/**
 * The toggle stops the click from reaching the row, which would open the
 * row's detail view instead of expanding. `subject` names the row for the
 * accessible label ("Show details for …").
 */
export function ExpanderToggleCell({
  open,
  onToggle,
  subject,
}: {
  open: boolean
  onToggle: () => void
  subject: string
}) {
  return (
    <TableCell sx={{ width: EXPANDER_COLUMN_WIDTH }} onClick={(e) => e.stopPropagation()}>
      <IconButton
        size="small"
        color="primary"
        aria-expanded={open}
        aria-label={open ? `Hide details for ${subject}` : `Show details for ${subject}`}
        onClick={onToggle}
      >
        {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
      </IconButton>
    </TableCell>
  )
}

/**
 * Rendered collapsed rather than omitted so the row keeps its rule and the
 * table does not reflow when one opens. `colSpan` is the table's column count.
 */
export function ExpanderRow({ open, colSpan, children }: { open: boolean; colSpan: number; children: ReactNode }) {
  return (
    <TableRow data-expander={open ? 'open' : 'closed'}>
      <TableCell colSpan={colSpan}>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Stack direction="row" spacing={4} useFlexGap sx={{ flexWrap: 'wrap', py: 1.5 }}>
            {children}
          </Stack>
        </Collapse>
      </TableCell>
    </TableRow>
  )
}

export function ExpanderField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {children}
    </Box>
  )
}
